import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";
import { Menu, X, ChevronDown, User, MessageSquare, LogOut, Settings, Calendar, Users, Globe, Trophy, Bot, Building2 } from "lucide-react";

const SPORTS = ["Tennis", "Pickleball", "Golf", "Table Tennis", "Skiing", "Padel", "Squash", "Badminton", "Climbing"];

const LANGUAGES = [
  { code: "en", label: "English", short: "EN" },
  { code: "es", label: "Español", short: "ES" },
  { code: "fr", label: "Français", short: "FR" },
  { code: "zh", label: "中文", short: "ZH" },
];

export default function Navbar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const { t, i18n } = useTranslation();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [langMenuOpen, setLangMenuOpen] = useState(false);
  const langMenuRef = useRef(null);
  const dropdownTimer = useRef(null);

  const openDropdown = (label) => {
    if (dropdownTimer.current) clearTimeout(dropdownTimer.current);
    setActiveDropdown(label);
  };
  const closeDropdown = () => {
    dropdownTimer.current = setTimeout(() => setActiveDropdown(null), 180);
  };

  const handleLangChange = (code) => {
    i18n.changeLanguage(code);
    localStorage.setItem("as_lang", code);
    setLangMenuOpen(false);
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMenuOpen(false); setActiveDropdown(null); setUserMenuOpen(false); }, [location]);

  useEffect(() => {
    const handler = (e) => {
      if (langMenuRef.current && !langMenuRef.current.contains(e.target)) setLangMenuOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const handleLogout = async () => { await logout(); navigate("/"); };

  const navLinks = [
    { label: t("nav.findPartners"), href: "/find-partners" },
    { label: t("nav.sports"), href: "/sports", dropdown: SPORTS },
    { label: t("nav.venues"), href: "/venues" },
    { label: "Group Sessions", href: "/group-sessions" },
    { label: "Markets", href: "/markets" },
    { label: t("nav.leagues"), href: "/leagues" },
    { label: "AI Concierge", href: "/concierge" },
    { label: t("nav.pricing"), href: "/pricing" },
  ];

  const currentLang = LANGUAGES.find(l => l.code === i18n.language) || LANGUAGES[0];

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-shadow duration-200 bg-white ${scrolled ? "shadow-md" : "shadow-sm"}`}
      aria-label="Main navigation"
    >
      {/* No skip link here: App.js already renders one at the top of the page.
          Two of them meant keyboard users tabbed through "Skip to main content"
          twice before reaching the navigation. */}

      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-between h-16">

          {/* Logo */}
          <Link to="/" className="flex items-center flex-shrink-0" data-testid="nav-logo">
            <img
              src="/logo.png"
              alt="ActivitySports"
              className="h-10 w-auto object-contain"
            />
          </Link>

          {/* Desktop nav */}
          <div className="hidden md:flex items-center gap-1">
            {navLinks.map((link) =>
              link.dropdown ? (
                <div
                  key={link.label}
                  className="relative"
                  onMouseEnter={() => openDropdown(link.label)}
                  onMouseLeave={closeDropdown}
                >
                  <button
                    className={`flex items-center gap-1 px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                      activeDropdown === link.label
                        ? "text-brand-green bg-brand-green/5"
                        : "text-brand-navy/80 hover:text-brand-navy hover:bg-gray-50"
                    }`}
                    data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                  >
                    {link.label}
                    <ChevronDown size={13} className={`transition-transform ${activeDropdown === link.label ? "rotate-180" : ""}`} />
                  </button>
                  {activeDropdown === link.label && (
                    <div className="absolute top-full left-0 mt-0.5 w-48 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-50">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item}
                          to={`/find-partners?sport=${item.toLowerCase().replace(/\s+/g, "-")}`}
                          className="block px-4 py-2.5 text-sm text-brand-navy/80 hover:text-brand-navy hover:bg-gray-50 transition-colors"
                        >
                          {item}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <Link
                  key={link.label}
                  to={link.href}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    location.pathname === link.href
                      ? "text-brand-green bg-brand-green/5"
                      : "text-brand-navy/80 hover:text-brand-navy hover:bg-gray-50"
                  }`}
                  data-testid={`nav-${link.label.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {link.label}
                </Link>
              )
            )}
          </div>

          {/* Right side — desktop */}
          <div className="hidden md:flex items-center gap-2">
            {/* Language switcher */}
            <div className="relative" ref={langMenuRef}>
              <button
                onClick={() => setLangMenuOpen(!langMenuOpen)}
                className="flex items-center gap-1.5 px-2.5 py-1.5 text-xs text-brand-navy/80 hover:text-brand-navy border border-gray-200 rounded-lg hover:border-gray-300 transition-colors"
                aria-label="Change language"
                data-testid="lang-switcher-btn"
              >
                <Globe size={13} />
                <span className="font-medium">{currentLang.short}</span>
              </button>
              {langMenuOpen && (
                <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-50" data-testid="lang-menu">
                  {LANGUAGES.map(lang => (
                    <button
                      key={lang.code}
                      onClick={() => handleLangChange(lang.code)}
                      className={`w-full text-left px-4 py-2.5 text-sm transition-colors hover:bg-gray-50 ${i18n.language === lang.code ? "text-brand-green font-semibold" : "text-brand-navy/80"}`}
                      data-testid={`lang-option-${lang.code}`}
                    >
                      {lang.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            {user ? (
              <div className="relative">
                <button
                  onClick={() => setUserMenuOpen(!userMenuOpen)}
                  className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-gray-200 hover:border-gray-300 transition-colors"
                  data-testid="user-menu-btn"
                >
                  {user.avatar_url ? (
                    <img src={user.avatar_url} alt={user.name} className="w-7 h-7 rounded-full object-cover" />
                  ) : (
                    <div className="w-7 h-7 rounded-full bg-brand-navy flex items-center justify-center dark-section">
                      <span className="text-xs font-bold text-white">{user.name?.[0]?.toUpperCase()}</span>
                    </div>
                  )}
                  <span className="text-sm font-medium text-brand-navy/80 max-w-[100px] truncate">{user.name}</span>
                  <ChevronDown size={12} className={`text-brand-navy/80 transition-transform ${userMenuOpen ? "rotate-180" : ""}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-gray-100 rounded-xl shadow-lg overflow-hidden z-50">
                    <div className="px-4 py-3 border-b border-gray-100">
                      <p className="text-sm font-bold text-brand-navy truncate">{user.name}</p>
                      <p className="text-xs text-gray-400 truncate">{user.email}</p>
                    </div>
                    {[
                      { icon: User, label: t("nav.myProfile"), href: `/profile/${user.id}` },
                      { icon: Settings, label: t("nav.myAccount"), href: "/account" },
                      { icon: MessageSquare, label: t("nav.messages"), href: "/messages" },
                      { icon: Calendar, label: t("nav.sessions"), href: "/sessions" },
                      { icon: Users, label: "My Partners", href: "/partners" },
                      { icon: Users, label: t("nav.clubs"), href: "/clubs" },
                      { icon: Trophy, label: t("nav.leagues"), href: "/leagues" },
                      { icon: Bot, label: "AI Concierge", href: "/concierge" },
                      { icon: Users, label: "Invite Friends", href: "/invite" },
                    ].map(({ icon: Icon, label, href }) => (
                      <Link key={href} to={href} className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-navy/80 hover:bg-gray-50 hover:text-brand-navy transition-colors">
                        <Icon size={15} className="text-gray-400" />
                        {label}
                      </Link>
                    ))}
                    {user.role === "facility_partner" && (
                      <Link to="/facility" className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-navy/80 hover:bg-gray-50 transition-colors" data-testid="nav-facility-portal">
                        <Building2 size={15} className="text-gray-400" />
                        Facility Portal
                      </Link>
                    )}
                    {user.role === "organizer" && (
                      <Link to="/organizer" className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-navy/80 hover:bg-gray-50 transition-colors" data-testid="nav-organizer-dashboard">
                        <Calendar size={15} className="text-gray-400" />
                        Organizer Dashboard
                      </Link>
                    )}
                    {user.role === "admin" && (
                      <Link to="/admin/verifications" className="flex items-center gap-3 px-4 py-2.5 text-sm text-brand-navy/80 hover:bg-gray-50 transition-colors">
                        <Settings size={15} className="text-gray-400" />
                        Admin — Verifications
                      </Link>
                    )}
                    <div className="border-t border-gray-100">
                      <button
                        onClick={handleLogout}
                        className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-500 hover:bg-red-50 transition-colors"
                        data-testid="nav-logout-btn"
                      >
                        <LogOut size={15} />
                        {t("nav.logout")}
                      </button>
                    </div>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-5 py-2 text-sm font-semibold text-brand-navy border-2 border-brand-navy rounded-full hover:bg-brand-navy hover:text-white transition-all dark-section"
                  data-testid="nav-login-btn"
                >
                  {t("nav.login")}
                </Link>
                <Link
                  to="/register"
                  className="px-5 py-2 text-sm font-bold text-white bg-brand-orange rounded-full hover:bg-brand-orange-hover transition-all shadow-sm"
                  data-testid="nav-register-btn"
                >
                  {t("nav.register")}
                </Link>
              </>
            )}
          </div>

          {/* Mobile hamburger */}
          <button
            className="md:hidden p-2 rounded-lg text-brand-navy/70 hover:text-brand-navy hover:bg-gray-100 transition-colors"
            onClick={() => setMenuOpen(!menuOpen)}
            aria-label={menuOpen ? "Close menu" : "Open menu"}
            aria-expanded={menuOpen}
            data-testid="mobile-menu-btn"
          >
            {menuOpen ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {menuOpen && (
        <div className="md:hidden bg-white border-t border-gray-100 shadow-lg" data-testid="mobile-menu">
          <div className="px-4 py-3 space-y-1">
            {navLinks.map((link) => (
              <Link
                key={link.label}
                to={link.href}
                className="block px-4 py-3 rounded-xl text-sm font-medium text-brand-navy/80 hover:text-brand-navy hover:bg-gray-50 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </div>
          <div className="px-4 py-3 border-t border-gray-100 space-y-2">
            {user ? (
              <>
                <Link to="/account" className="block px-4 py-3 rounded-xl text-sm font-medium text-brand-navy/80 hover:bg-gray-50">
                  {t("nav.myAccount")}
                </Link>
                <button
                  onClick={handleLogout}
                  className="w-full text-left px-4 py-3 rounded-xl text-sm font-medium text-red-500 hover:bg-red-50"
                >
                  {t("nav.logout")}
                </button>
              </>
            ) : (
              <>
                <Link to="/login" className="block w-full text-center px-5 py-2.5 border-2 border-brand-navy text-brand-navy text-sm font-semibold rounded-full hover:bg-brand-navy hover:text-white transition-all dark-section">
                  {t("nav.login")}
                </Link>
                <Link to="/register" className="block w-full text-center px-5 py-2.5 bg-brand-orange text-white text-sm font-bold rounded-full hover:bg-brand-orange-hover transition-all">
                  {t("nav.register")}
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
